import type { UniversalModifierValue } from "schemas/generated/fileModifiers";

import React from "react";

import ButtonModifierBlock from "@/features/react-editor/components/modifiers/ButtonModifierBlock";
import ContainerModifierBlock from "@/features/react-editor/components/modifiers/ContainerModifierBlock";
import { useModifiers } from "@/features/react-editor/components/modifiers/hooks/useModifiers";
import IconModifierBlock from "@/features/react-editor/components/modifiers/IconModifierBlock";
import ImageModifierBlock from "@/features/react-editor/components/modifiers/ImageModifierBlock";
import {
  useDefaultSelectedElementDesignation,
  useModifiersStoreActions,
} from "@/features/react-editor/components/modifiers/store/ModifiersStoreContext";
import TextModifierBlock from "@/features/react-editor/components/modifiers/TextModifierBlock";
import IconButton from "@replo/design-system/components/button/IconButton";
import { Trash2, X } from "lucide-react";

import LinkModifierBlock from "./LinkModifierBlock";
import VideoModifierBlock from "./VideoModifierBlock";

function UnknownModifierBlock() {
  return (
    <div className="text-sm text-muted">
      This element cannot be modified directly. Please use the chat to change
      this element.
    </div>
  );
}

const MODIFIER_BLOCKS = {
  text: {
    displayName: "Text",
    component: TextModifierBlock,
  },
  container: {
    displayName: "Container",
    component: ContainerModifierBlock,
  },
  button: {
    displayName: "Button",
    component: ButtonModifierBlock,
  },
  image: {
    displayName: "Image",
    component: ImageModifierBlock,
  },
  link: {
    displayName: "Link",
    component: LinkModifierBlock,
  },
  video: {
    displayName: "Video",
    component: VideoModifierBlock,
  },
  icon: {
    displayName: "Icon",
    component: IconModifierBlock,
  },
  "product-section": {
    displayName: "Product Section",
    component: ContainerModifierBlock,
  },
  unknown: {
    displayName: "Unknown",
    component: UnknownModifierBlock,
  },
} as const;

export default function Modifiers() {
  const modifierActions = useModifiersStoreActions();
  const defaultDesignation = useDefaultSelectedElementDesignation();

  const { applyModifier } = useModifiers();

  const handleModifiersChange = async (
    modifiers: UniversalModifierValue[],
    byteOffset: number,
  ) => {
    for (const modifier of modifiers) {
      await applyModifier(modifier, byteOffset);
    }
  };

  if (!defaultDesignation) {
    return null;
  }

  const elementType = defaultDesignation.element.type;
  const block = MODIFIER_BLOCKS[elementType];
  const ModifierComponent = block.component;

  return (
    <div className="flex max-h-full h-full overflow-auto w-full p-4 flex-col gap-4 flex-start border-border rounded-xl bg-white border-0.5">
      <div className="flex items-center justify-between flex-shrink-0">
        <div className="flex items-center header-h4">
          {defaultDesignation
            ? MODIFIER_BLOCKS[defaultDesignation.element.type].displayName
            : "Selected element"}
        </div>
        <div className="flex items-center gap-3">
          <IconButton
            variant="tertiary"
            size="sm"
            icon={<Trash2 className="w-4 h-4" />}
            onClick={() =>
              void applyModifier(
                {
                  type: "delete",
                },
                defaultDesignation.element.byteOffset,
              )
            }
          />
          <IconButton
            variant="tertiary"
            size="sm"
            icon={<X className="w-4 h-4" />}
            onClick={() => {
              modifierActions.toggleDisplayMode();
            }}
          />
        </div>
      </div>
      <div className="border-t-0.5 border-border -mx-4 flex-shrink-0" />
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto overflow-x-hidden no-scrollbar">
        <ModifierComponent
          onModifiersValueChange={(modifiers, byteOffset) => {
            void handleModifiersChange(modifiers, byteOffset);
          }}
        />
      </div>
    </div>
  );
}
